import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "222",
  "slug": "introduction-remote-sensing-beginners-guide",
  "title": "Introduction to Remote Sensing: A Beginner's Guide",
  "excerpt": "Remote sensing powers everything from weather forecasts to crop yield estimates to flood disaster response. Here is a plain-language introduction to how it works and why it matters.",
  "date": "2026-03-11",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80",
  "readTime": "2 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Every time a weather forecast is broadcast, a wildfire is tracked, a crop failure is predicted, or a flood plain is mapped, remote sensing is involved. It is one of the most consequential technologies of the past 60 years. This guide starts from first principles and builds up to where modern remote sensing is heading."
    },
    {
      "type": "heading",
      "text": "What Is Remote Sensing?"
    },
    {
      "type": "paragraph",
      "text": "Remote sensing is the acquisition of information about an object or surface without direct physical contact. In geographic terms, this almost always means collecting data about the Earth's surface from aircraft or satellites. The sensor records electromagnetic radiation — either reflected sunlight or energy emitted by the surface itself."
    },
    {
      "type": "paragraph",
      "text": "The key distinction is between passive and active sensors. Passive sensors detect naturally occurring radiation — primarily reflected sunlight. Active sensors emit their own energy and measure what bounces back. Radar and LiDAR are both active systems that can penetrate clouds and work at night."
    },
    {
      "type": "heading",
      "text": "The Electromagnetic Spectrum"
    },
    {
      "type": "list",
      "items": [
        "Visible (0.4–0.7 μm) — what our eyes see. Used in true-colour imagery for visual interpretation.",
        "Near-infrared (0.7–1.3 μm) — strongly reflected by healthy vegetation. The basis of the NDVI vegetation index.",
        "Shortwave infrared (1.3–2.5 μm) — sensitive to soil moisture and mineral composition.",
        "Thermal infrared (8–14 μm) — measures surface temperature. Used for urban heat islands and fire detection.",
        "Microwave/radar (1 mm–1 m) — penetrates clouds and vegetation. Used for flood mapping and terrain modelling."
      ]
    },
    {
      "type": "heading",
      "text": "Key Satellites You Should Know"
    },
    {
      "type": "paragraph",
      "text": "The Landsat programme has been continuously imaging the Earth since 1972. It provides the longest continuous satellite record of Earth's land surface and is freely available. The European Space Agency's Sentinel series provides complementary free data — Sentinel-2 delivers high-resolution multispectral imagery with a five-day revisit time."
    },
    {
      "type": "heading",
      "text": "Building Visual Intuition for Satellite Imagery"
    },
    {
      "type": "paragraph",
      "text": "One underrated skill in remote sensing education is learning to interpret imagery visually — before reaching for an algorithm. Tools like EarthGuessr, which challenges you to identify locations from satellite views on a 3D globe, are a surprisingly effective way to build pattern recognition. Repeated exposure to global imagery trains your eye to notice the cues that distinguish one region from another."
    }
  ]
};
